import type { ReactNode } from 'react';
import { Card, CardContent } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';

const cardClassName = 'min-w-0 rounded-section border-border/70 shadow-none';

/** Same shell and title/subtitle as the loaded usage cards. */
const SkeletonCard = ({ action, children }: { action?: ReactNode; children: ReactNode }) => (
  <Card className={cardClassName}>
    <CardContent className="flex h-full flex-col gap-5 p-5 md:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4 w-40" />
        </div>
        {action}
      </div>
      {children}
    </CardContent>
  </Card>
);

/** A label and value on one line with a bar under it, like the token and share rows. */
const BarRow = ({ barClassName }: { barClassName: string }) => (
  <div className="space-y-1.5">
    <div className="flex items-center justify-between gap-3">
      <Skeleton className="h-4 w-28" />
      <Skeleton className="h-4 w-20" />
    </div>
    <Skeleton className={`w-full rounded-full ${barClassName}`} />
  </div>
);

const rows = (count: number) => Array.from({ length: count }, (_, index) => index);

/** Loading state for the usage cards; Organization has a third "Users" tab in the table card. */
export function UsageLoadingSkeleton({ showUsers = false }: { showUsers?: boolean }) {
  return (
    <div className="flex flex-col gap-6">
      <div className="grid items-stretch gap-x-4 gap-y-6 lg:grid-cols-5">
        <div className="min-w-0 lg:col-span-3 [&>*]:h-full">
          <SkeletonCard>
            <div className="flex items-center justify-between gap-3 border-b border-border pb-3">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-36" />
            </div>
            <div className="space-y-4">
              {rows(4).map((index) => (
                <div key={index} className="space-y-1.5">
                  <BarRow barClassName="h-2" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              ))}
            </div>
            <div className="border-t border-border pt-4">
              <Skeleton className="h-3 w-1/2" />
            </div>
          </SkeletonCard>
        </div>
        <div className="min-w-0 lg:col-span-2 [&>*]:h-full">
          <SkeletonCard>
            <div>
              {rows(5).map((index) => (
                <div
                  key={index}
                  className="flex items-start justify-between gap-3 border-b border-border py-3 first:pt-0 last:border-b-0 last:pb-0"
                >
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-3 w-36" />
                  </div>
                  <Skeleton className="h-4 w-12" />
                </div>
              ))}
            </div>
          </SkeletonCard>
        </div>
      </div>

      <SkeletonCard>
        <Skeleton className="h-60 w-full sm:h-72" />
      </SkeletonCard>

      <SkeletonCard
        action={
          <div className="flex gap-1 rounded-full border border-border p-1">
            {rows(showUsers ? 3 : 2).map((index) => (
              <Skeleton key={index} className="h-7 flex-1 rounded-full sm:w-16 sm:flex-none" />
            ))}
          </div>
        }
      >
        <div>
          <div className="border-b border-border pb-3">
            <Skeleton className="h-3 w-full" />
          </div>
          {rows(4).map((index) => (
            <div key={index} className="flex items-center justify-between gap-4 border-b border-border py-4">
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-20" />
              </div>
              <Skeleton className="h-2 w-24 rounded-full sm:w-40" />
            </div>
          ))}
        </div>
      </SkeletonCard>

      <div className="grid items-stretch gap-x-4 gap-y-6 xl:grid-cols-2">
        {rows(2).map((card) => (
          <SkeletonCard key={card}>
            <div className="space-y-3">
              {rows(3).map((index) => (
                <BarRow key={index} barClassName="h-1.5" />
              ))}
            </div>
          </SkeletonCard>
        ))}
      </div>
    </div>
  );
}
